import { z } from 'zod';

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, 'O endereço precisa de pelo menos 2 caracteres')
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use apenas letras minúsculas, números e hífen');

export const brazilianStateSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(2, 'Use a sigla do estado, ex.: SP');

export const congregationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  isHeadquarters: z.boolean(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  capacity: z.number().int().nullable(),
  responsibleName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Congregation = z.infer<typeof congregationSchema>;

export const createCongregationSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da congregação').max(160),
  slug: slugSchema,
  isHeadquarters: z.boolean().default(false),
  addressLine: z.string().trim().max(240).nullish(),
  city: z.string().trim().max(120).nullish(),
  state: brazilianStateSchema.nullish(),
  capacity: z.coerce.number().int().positive().max(100_000).nullish(),
  responsibleName: z.string().trim().max(160).nullish(),
});
export type CreateCongregationInput = z.infer<typeof createCongregationSchema>;

export const updateTenantSettingsSchema = z.object({
  name: z.string().trim().min(3).max(160).optional(),
  denomination: z.string().trim().max(80).nullish(),
  timezone: z.string().trim().max(64).optional(),
  brandColor: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Informe a cor em hexadecimal, ex.: #4F46E5')
    .optional(),
  logoUrl: z.string().url().max(500).nullish(),
  /** Horas de antecedência com que a igreja cobra a confirmação (M08). */
  confirmationDeadlineHours: z
    .number()
    .int()
    .min(2, 'Menos de 2 horas não dá tempo de ninguém responder')
    .max(336, 'Duas semanas é o máximo: escala cobrada cedo demais é esquecida')
    .optional(),
  /**
   * Por quantos meses a igreja guarda o que comunicou (seção 8.4). Dado
   * pessoal não se guarda para sempre "por via das dúvidas".
   */
  retentionMonths: z
    .number()
    .int()
    .min(6, 'Menos de 6 meses não responde "e o mês passado?"')
    .max(120, 'Dez anos é o máximo')
    .optional(),
});
export type UpdateTenantSettingsInput = z.infer<typeof updateTenantSettingsSchema>;

export const tenantDetailSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  denomination: z.string().nullable(),
  timezone: z.string(),
  brandColor: z.string(),
  logoUrl: z.string().nullable(),
  status: z.string(),
  trialEndsAt: z.string().datetime().nullable(),
  confirmationDeadlineHours: z.number().int(),
  retentionMonths: z.number().int(),
});
export type TenantDetail = z.infer<typeof tenantDetailSchema>;
